import { HOMEWORK_MAX_EXERCISES, levelName } from "@edu/shared";
import { ApiError, type ApiErrorCode } from "../../../lib/api.ts";

/**
 * Слова отказов формы выдачи.
 *
 * Их две группы, и они выглядят на экране по-разному, потому что означают разное
 * (1.6 docs/homework-phase2-design.md): отказ «на одном» это хвост строки после
 * имени человека, отказ «на всех» это одна фраза вместо всей панели. Общий текст
 * на весь экран читался бы в строке как «Марко: домашка не создана: ученик
 * отвязан».
 *
 * ⚠️ Свой `failureText` для строки был и у пачки уроков нескольким ученикам, по
 * той же причине. Пачки с 17.09.2026 нет (она заводила урок без даты и ушла
 * вместе с ним), и разделение на две группы держится теперь только здесь.
 */

/** Хвост строки после имени ученика. Без заглавной буквы и без точки. */
export function rowFailureText(code: ApiErrorCode): string {
  switch (code) {
    case "student_not_linked":
      return "ученик отвязан, отправьте ему новую ссылку";
    case "student_not_found":
      return "ученик не найден, обновите страницу";
    case "lesson_not_found":
      return "занятие не найдено, снимите подпись «задана после занятия»";
    case "request_id_reused":
      // Ключ придумывает клиент, и столкновение означает, что план приехал из
      // другой вкладки или другого преподавателя. Молча пересоздавать нельзя
      return "эту домашку уже задавали из другой вкладки, начните заново";
    case "homework_not_found":
      return "домашка не найдена, обновите страницу";
    case "internal":
      // ⚠️ Ветка не декоративная. Сборка заданий из генераторов бросает
      // исключением всё, что считает своей поломкой (исчерпание перебросов,
      // разъезд реестра с объявлением), и Fastify отвечает на это `internal`.
      // Без этой строки в ряду ученика печаталось «Марко: internal»
      return "сбой на сервере, задайте этому ученику ещё раз";
    default:
      // Незнакомый код это тоже наша недоработка, а не слово для человека:
      // сам код оставлен затем, чтобы его можно было назвать в поддержке
      return `сбой на сервере (${code}), задайте этому ученику ещё раз`;
  }
}

/** Строка «связь пропала»: круга по сети не хватило, а не отказали по существу. */
export const NETWORK_ROW_TEXT = "связь пропала, повторите";

/**
 * Ученик, до которого цикл не дошёл. ⚠️ Это НЕ отказ, а неизвестность, и слово
 * обязано быть другим: «не создано» человек прочитал бы как «сервер отказал».
 */
export const UNSENT_ROW_TEXT = "не успели задать";

/**
 * Отказ, повторяющийся у всех: показывается один раз и вместо всей панели.
 *
 * ⚠️ Тексты здесь написаны для УСТАРЕВШЕЙ вкладки, и это стоит помнить, читая
 * их. Форма ограничивает заказ по справочнику и не отправляет заданий без
 * верного ответа, поэтому свежая вкладка почти ни до одного из этих отказов не
 * доходит; доходит та, что открыта часами, пока справочник или заготовка
 * менялись. Отсюда «обновите страницу» в большинстве строк и отсутствие в них
 * намёка, что виноват человек.
 */
export function allFailureText(
  e: unknown,
  /**
   * Название темы по слагу. Приходит из справочника формы: в отказе сервера
   * лежит слаг, а преподаватель выбирал название, и «percentages» ему ни о чём
   * не говорит.
   */
  topicName?: (skill: string) => string | null,
): string {
  if (e instanceof ApiError) {
    /*
      ⚠️ Этот отказ страховочный, а не рабочий: форма ограничивает и уровень, и
      количество по справочнику, то есть до сервера такой заказ дойти не должен.
      Дойти он может честно: реестр меняется выкаткой, а вкладка живёт часами.
      Поэтому в каждом тексте стоит «обновите страницу» и ни в одном нет намёка,
      что виноват человек.
    */
    if (e.code === "generator_unavailable") {
      const skill = typeof e.details.skill === "string" ? e.details.skill : null;
      const named = skill === null ? null : (topicName?.(skill) ?? null);
      // Уровень называется вместе с темой: одна тема может стоять в форме
      // двумя строками разного уровня, и потолок у них разный
      const at = typeof e.details.difficulty === "number"
        ? ` (${levelName(e.details.difficulty)} уровень)`
        : "";
      const what = named === null ? "по выбранной теме" : `по теме «${named}»${at}`;
      if (e.details.reason === "too-many-tasks") {
        /*
          ⚠️ Совета «обновите страницу» здесь НЕТ, и это не забывчивость.
          Заказ невыполним, а не устарел: обновление теряет набранный план и
          возвращает тот же отказ. Так и было (находка ревью 18.08.2026).
          Поправить можно только одним способом, и он назван словами.
        */
        const max = typeof e.details.maxCount === "number" ? e.details.maxCount : null;
        return max === null
          ? `Столько заданий ${what} не собрать: уменьшите количество по этой теме.`
          : `Заданий ${what} можно задать не больше ${max}: уменьшите количество по этой теме.`;
      }
      const anyLevel = Array.isArray(e.details.available) && e.details.available.length === 0;
      return anyLevel
        ? `Заданий ${what} больше нет. Обновите страницу.`
        : `Заданий выбранного уровня ${what} больше нет. Обновите страницу.`;
    }
    /*
      ⚠️ Ветки `exercise_key_missing` здесь БОЛЬШЕ НЕТ, и это не пропуск. С
      03.09.2026 компиляция домашки такой отказ не выдаёт вовсе: задание без
      верного ответа она молча не берёт в ленту, потому что отказ приходил бы не
      тому, кто ошибся, а тому, кто в этот момент пишет соседнее задание
      (`apps/api/src/homework-exercises.ts`). Сам код при этом жив и приходит с
      создания УРОКА, где его разбирает `features/lessons/lesson-errors.ts`.
      Текст, оставленный здесь, был бы словами для отказа, которого не бывает, а
      найти это можно было бы только чтением сервера.
    */
    if (e.code === "homework_empty") {
      /*
        ⚠️ Раньше здесь стояло «Не выбрано ни одного задания», и это могло быть
        неправдой: отказ приходит и тогда, когда отмеченные задания есть, но ни
        у одного нет верного ответа. Свежая вкладка досюда не доходит (форма
        такие задания не отправляет и держит кнопку недоступной), а устаревшая
        доходит, и ей нужен текст, верный в обоих случаях.
      */
      /*
        С 26.09.2026 сюда приходит и выбранная заготовка без единого отмеченного
        задания: форма шлёт её пустым составом нарочно (1.4
        docs/homework-scratch-design.md), и выход надо назвать оба, задания или
        материал без заготовки
      */
      return "В домашке нет ни одного задания. Отметьте задания или уберите выбор заготовки, чтобы задать только материал.";
    }
    if (e.code === "file_not_found") {
      /*
        Картинка материала не годится: чаще всего её убрали в корзину, пока
        незаконченная раздача лежала (2 docs/homework-scratch-design.md).
        Картинки у всех работ раздачи одни и те же, поэтому отказ «на всех»
      */
      return "Одна из картинок удалена из файлов. Уберите её и задайте домашку снова.";
    }
    if (e.code === "homework_full") {
      return `Заданий слишком много: в одной домашке не больше ${HOMEWORK_MAX_EXERCISES}.`;
    }
    if (e.code === "plan_not_found") return "Заготовка удалена или недоступна.";
    if (e.code === "invalid_exercise_source") {
      return "Задание в заготовке собрано неверно: откройте её в редакторе.";
    }
    if (e.code === "invalid_body") return "Вкладка устарела, обновите страницу.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
